/**
 * The VTK.wasm rendering layer.
 *
 * Everything that touches the WebAssembly runtime lives here so the rest of
 * the app deals in plain data. The session's `vtk` namespace is a proxy over
 * the C++ API: methods are camelCased (`SetBackground` -> `setBackground`)
 * and anything marked `maySuspend` in the bundle -- `render()` among them --
 * may return a promise, so those calls are awaited.
 *
 * The bundle ships no IO module, which is why nothing here parses a file. The
 * server sends buffers (see backend/flowlab/viewer/surface_extractor.py) and
 * this module copies them onto the wasm heap and assembles a native
 * `vtkPolyData`.
 *
 * **Boundary-group highlighting reuses the artifact, and adds nothing to it.**
 * Gmsh's legacy VTK writer can emit a per-cell `CellEntityIds` array, and where
 * it does, those ids are the *elementary entity tags* -- the same tag space
 * `Generation.boundary_map` records against each boundary group id. So a
 * selection is resolvable entirely in the browser: look up the group's tags,
 * mark the cells carrying them, colour through a two-entry table. There is no
 * second viewer data model, no new artifact, and no request that the mesher
 * write anything it does not already write.
 *
 * **Measured against a real Gmsh 4.15.2 mesh, FlowLab's own `mesh.vtk` carries
 * no such array.** The legacy writer emits `CELL_DATA` only when physical groups
 * are defined, and `docker/gmsh/driver.py` defines none -- deliberately, since
 * defining any would change mesh bytes and invalidate the determinism baseline
 * Step 4 pinned. A unit box writes 1760 cells and zero cell arrays. Highlighting
 * is therefore a capability the *artifact* has to supply, and today's does not.
 *
 * Highlighting is consequently written as a *capability*, not an assumption: a
 * surface with no entity array reports `supportsSelection === false` and the
 * panel says so, rather than silently highlighting nothing. That is the path
 * this deployment actually takes; the code above it is what makes the other path
 * work the day a mesh arrives carrying the array (a `.msh` ingest whose physical
 * groups survive, or a driver revision that opts into writing them).
 */

import { loadAsync } from '@kitware/vtk-wasm';

import { SLICE_NORMALS, slicePlaneOrigin } from './slice-plane.js';

// Emscripten resolves canvases registered in specialHTMLTargets by a key that
// must start with "!"; this avoids depending on a DOM id.
const CANVAS_KEY = '!flowlab-viewer-canvas';

// The render area is part of the page, so it carries the page's graphite: a
// near-black floor lifting to the panel surface at the top. Values are the
// sRGB channels of --graphite-900 / --graphite-700 in style.css.
const BACKGROUND = [0.055, 0.051, 0.051];
const BACKGROUND2 = [0.102, 0.094, 0.086];
// Warm off-white, lit rather than tinted -- the mesh reads as a machined
// surface instead of as a coloured object.
const SURFACE_COLOR = [0.839, 0.804, 0.749];
const EDGE_COLOR = [0.184, 0.176, 0.172];

// The identity accent, used for exactly one thing in the scene: the boundary
// group the inspector has selected.
const SELECTION_COLOR = [0.776, 0.176, 0.235];
// What an unselected cell reads as while a selection is active. Dimmed from
// SURFACE_COLOR rather than greyed, so the accent is the only bright thing.
const DESELECTED_COLOR = [0.408, 0.384, 0.361];

// vtkMapper::VTK_GET_ARRAY_BY_NAME
const ARRAY_ACCESS_BY_NAME = 1;

/**
 * The per-cell elementary entity tag, as Gmsh's VTK writer names it.
 *
 * Not configurable and not negotiated: it is the array that mesher writes, and
 * a surface without it is a surface whose regions cannot be told apart.
 */
export const ENTITY_ARRAY = 'CellEntityIds';

/** The derived array the selection is coloured through. Never served, never stored. */
const SELECTION_ARRAY = 'FlowLabSelection';

export class ViewerRuntimeError extends Error {
  constructor(message, options) {
    super(message, options);
    this.name = 'ViewerRuntimeError';
  }
}

/**
 * A live VTK.wasm scene bound to one canvas.
 *
 * Construct via {@link createScene}, then call {@link VtkScene#setSurface} to
 * display a decoded payload.
 */
export class VtkScene {
  #session;
  #vtk;
  #renderWindow;
  #renderer;
  #interactor;
  #mapper;
  #actor;
  #lookupTable;
  #selectionTable;
  #scalarBar;
  #axesWidget;
  #canvas;
  #container;
  #resizeObserver;
  // Set by setSurface: a camera fitted before the viewport had a usable box
  // describes nothing, so the fit waits for one. Cleared by the fit itself.
  #needsCameraFit = false;
  // The pan gesture's own DOM listeners, kept so dispose() can take them off
  // again: the move and release are on window, not the canvas, so a scene that
  // is replaced would otherwise leave them driving a dead interactor.
  #panHandlers = null;
  #polyData = null;
  #arrays = [];
  #activeArray = null;
  #renderScheduled = false;
  #disposed = false;

  // Selection. #entityIds is the payload's own CellEntityIds values, kept so a
  // selection is a CPU pass over numbers already in hand rather than a refetch;
  // #selectionKey is the tag set currently uploaded, so re-selecting the same
  // group is free and every distinct selection uploads its array exactly once.
  #entityIds = null;
  #selectionKey = null;
  #selectionActive = false;

  // Slicing. The plane and the clip filter are built once in #build(); only the
  // plane's origin/normal change afterwards, and #mapperReadsClipper tracks
  // which input the mapper is currently wired to.
  #clipPlane;
  #clipper;
  #clipOutputPort = null;
  #mapperReadsClipper = false;
  #bounds = null;
  #sliceEnabled = false;
  #sliceAxis = 'x';
  #slicePercent = 50;

  constructor({ session, canvas, container }) {
    this.#session = session;
    this.#vtk = session.vtk;
    this.#canvas = canvas;
    this.#container = container;
    this.#build();
  }

  /** Field arrays available for colouring, in payload order. */
  get arrays() {
    return this.#arrays.map(({ name, location, components, range }) => ({
      name,
      location,
      components,
      range,
    }));
  }

  /**
   * Whether this surface can distinguish boundary groups.
   *
   * False when the mesher wrote no entity ids -- which is a fact about the
   * artifact, so the UI reports it rather than offering a highlight that would
   * do nothing.
   */
  get supportsSelection() {
    return this.#entityIds !== null;
  }

  /** The distinct entity tags present on this surface, ascending. */
  get entityTags() {
    if (!this.#entityIds) return [];
    return [...new Set(this.#entityIds)].sort((a, b) => a - b);
  }

  /** Assemble the renderer, render window, interactor and mapper. */
  #build() {
    const vtk = this.#vtk;

    this.#renderer = vtk.vtkRenderer();
    this.#renderer.setBackground(...BACKGROUND);
    this.#renderer.setBackground2(...BACKGROUND2);
    this.#renderer.setGradientBackground(true);

    // Register the canvas so both the render window and the interactor can
    // address it without the element needing a DOM id.
    this.#session.registerCanvas(CANVAS_KEY, this.#canvas);

    this.#renderWindow = vtk.vtkWebAssemblyOpenGLRenderWindow();
    this.#renderWindow.setCanvasSelector(CANVAS_KEY);
    this.#renderWindow.addRenderer(this.#renderer);
    // 8x MSAA is affordable on a surface mesh and removes the stair-stepping
    // that makes CFD geometry hard to read.
    this.#renderWindow.setMultiSamples(8);

    this.#interactor = vtk.vtkWebAssemblyRenderWindowInteractor();
    this.#interactor.setCanvasSelector(CANVAS_KEY);
    // Sizing is driven from #syncSize() against the real layout box, so VTK's
    // own canvas management is turned off to keep one source of truth.
    this.#interactor.setExpandCanvasToContainer(false);
    this.#interactor.setInstallHTMLResizeObserver(false);
    this.#interactor.setRenderWindow(this.#renderWindow);
    this.#interactor.setInteractorStyle(vtk.vtkInteractorStyleTrackballCamera());
    // Before initialize(), which is when VTK attaches its own mouse listeners:
    // these have to be first on the canvas so a pan gesture can be claimed
    // before the interactor sees it as a rotate.
    this.#installPanControls();
    this.#interactor.initialize();

    this.#mapper = vtk.vtkPolyDataMapper();
    this.#actor = vtk.vtkActor();
    this.#actor.setMapper(this.#mapper);

    const property = this.#actor.getProperty();
    property.setColor(...SURFACE_COLOR);
    property.setEdgeColor(...EDGE_COLOR);
    property.setInterpolationToPhong();
    this.#renderer.addActor(this.#actor);

    // The clipping pipeline is assembled once and left idle until the slice is
    // switched on. vtkClipPolyData tracks its clip function's MTime, so moving
    // #clipPlane is enough to make the filter re-execute on the next render --
    // no re-upload of the surface and no rebuilt pipeline per slider tick.
    this.#clipPlane = vtk.vtkPlane();
    this.#clipper = vtk.vtkClipPolyData();
    this.#clipper.setClipFunction(this.#clipPlane);
    // Clip exactly on the plane, and leave the input scalars alone so colouring
    // by a field keeps working on the clipped output.
    this.#clipper.setValue(0);
    this.#clipper.setGenerateClipScalars(0);

    this.#lookupTable = vtk.vtkLookupTable();
    // A warm ramp rather than the CFD blue->red: this page has one accent
    // colour and a scalar field is not it, so the field ramp runs through the
    // page's own warm range and leaves burgundy meaning "selected".
    this.#lookupTable.setHueRange(0.14, 0.02);
    this.#lookupTable.setSaturationRange(0.12, 0.78);
    this.#lookupTable.setValueRange(0.96, 0.62);
    this.#lookupTable.setNumberOfTableValues(256);
    this.#lookupTable.build();
    this.#mapper.setLookupTable(this.#lookupTable);

    // Two entries, because a selection has two states. Built once; only which
    // table the mapper reads changes.
    this.#selectionTable = vtk.vtkLookupTable();
    this.#selectionTable.setNumberOfTableValues(2);
    this.#selectionTable.setTableRange([0, 1]);
    this.#selectionTable.setTableValue(0, ...DESELECTED_COLOR, 1);
    this.#selectionTable.setTableValue(1, ...SELECTION_COLOR, 1);
    this.#selectionTable.build();

    this.#scalarBar = vtk.vtkScalarBarActor();
    this.#scalarBar.setLookupTable(this.#lookupTable);
    this.#scalarBar.setNumberOfLabels(5);
    this.#scalarBar.setOrientationToVertical();
    this.#scalarBar.setVisibility(0);
    this.#renderer.addViewProp(this.#scalarBar);

    this.#buildOrientationMarker();
    this.#observeResize();

    // Starting the interactor installs the DOM event handlers that make the
    // camera respond to the mouse. It is not a blocking loop in the browser
    // build, but a failure here must not take the whole viewer down.
    try {
      this.#interactor.start();
    } catch (cause) {
      console.warn('Interactor start failed; camera interaction may be limited', cause);
    }
  }

  /**
   * Make panning reachable, without writing a camera of our own.
   *
   * `vtkInteractorStyleTrackballCamera` already implements pan -- it is the same
   * object that rotates, and it pans in response to VTK's own `StartPanEvent` /
   * `PanEvent` / `EndPanEvent`. What it does not do is bind those events to a
   * gesture a trackpad or a browser makes easy to perform, and what the browser
   * makes *hard* is the middle button: on a wheel-less mouse it does not exist,
   * and where it does, middle-click autoscroll competes with it. So the style was
   * panning-capable and effectively unreachable.
   *
   * Two routes to the existing implementation, both of which call into VTK and
   * change nothing about the camera:
   *
   * - **Middle drag**, which the style already handles natively. The only thing
   *   missing was `preventDefault`, so the browser's autoscroll does not eat the
   *   gesture. Left alone otherwise, because the interactor is already bound to
   *   it.
   * - **Shift + left drag**, forwarded to `StartPanEvent` / `PanEvent` /
   *   `EndPanEvent`. This is the mapping every CAD viewport uses, it works with
   *   one button and no modifier ambiguity, and the pan maths is VTK's.
   *
   * The modifier case has to intercept the pointer before VTK's own listener
   * runs, or the interactor would treat the same drag as a rotate. Hence
   * capture-phase on the canvas, and setting both the current and last event
   * position: the interactor normally advances that pair itself as it consumes
   * an event, and a hand-driven event that skips it measures a zero delta.
   */
  #installPanControls() {
    const canvas = this.#canvas;
    let panning = false;

    // VTK's own DOM handler reports positions bottom-up, because the renderer's
    // origin is. The flip-Y form is used for exactly that reason: passing
    // top-down pixel coordinates to the plain setter would make a vertical pan
    // move the model the opposite way to the interactor's own middle-drag.
    const at = (event) => {
      const rect = canvas.getBoundingClientRect();
      return [
        event.clientX - rect.left,
        rect.height - (event.clientY - rect.top),
      ];
    };

    // The interactor's own event handling advances "last position" from the
    // event it just processed. Driving events by hand means doing that step
    // explicitly, or every Pan would measure a zero delta and the model would
    // never move. Shift is reported so the style sees the same modifier state
    // VTK would have given it.
    const feed = (name, event) => {
      const [x, y] = at(event);
      const info = (x2, y2) => [x2, y2, 0, event.shiftKey ? 1 : 0, 0, 0, null];
      this.#interactor.setEventPositionFlipY(...info(x, y));
      this.#interactor.setLastEventPosition(x, y);
      this.#interactor[name]();
      this.scheduleRender();
    };

    const onDown = (event) => {
      // Middle button: leave the gesture to the interactor, which already pans
      // on it, and only stop the browser from starting an autoscroll.
      if (event.button === 1) {
        event.preventDefault();
        return;
      }
      if (event.button !== 0 || !event.shiftKey) {
        return;
      }
      // Claimed, so VTK's listener never sees this as a left-drag rotate.
      event.stopImmediatePropagation();
      event.preventDefault();
      panning = true;
      canvas.classList.add('stage__canvas--panning');
      feed('startPanEvent', event);
    };

    const onMove = (event) => {
      if (!panning) {
        return;
      }
      event.stopImmediatePropagation();
      feed('panEvent', event);
    };

    const onUp = (event) => {
      if (!panning) {
        return;
      }
      event.stopImmediatePropagation();
      panning = false;
      canvas.classList.remove('stage__canvas--panning');
      feed('endPanEvent', event);
    };

    // The browser also autoscrolls on middle-click *release*, and shift+drag
    // would otherwise leave a text selection behind.
    const onAux = (event) => {
      if (event.button === 1) {
        event.preventDefault();
      }
    };

    canvas.addEventListener('mousedown', onDown, true);
    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('mouseup', onUp, true);
    canvas.addEventListener('auxclick', onAux);
    this.#panHandlers = { onDown, onMove, onUp, onAux };
  }

  /** Add the corner axes triad that shows the current orientation. */
  #buildOrientationMarker() {
    try {
      const axes = this.#vtk.vtkAxesActor();
      const widget = this.#vtk.vtkOrientationMarkerWidget();
      widget.setOrientationMarker(axes);
      widget.setInteractor(this.#interactor);
      widget.setViewport(0.0, 0.0, 0.16, 0.22);
      widget.setEnabled(1);
      widget.setInteractive(0);
      this.#axesWidget = widget;
    } catch (cause) {
      // Purely decorative: never let it break the render path.
      console.warn('Orientation marker unavailable', cause);
    }
  }

  /**
   * Upload a decoded surface payload and display it.
   *
   * @param {ReturnType<import('./surface-payload.js').decodeSurface>} surface
   */
  setSurface(surface) {
    this.#assertLive();
    const vtk = this.#vtk;
    const heap = this.#session.typedArrayInterface;

    // toVTKAoSArray copies each buffer onto the wasm heap once and wraps it in
    // the matching vtkTypeXxxArray -- the whole transfer, with no JSON and no
    // per-element loop on either side.
    const points = vtk.vtkPoints();
    points.setData(heap.toVTKAoSArray(surface.points, 3, 'Points'));

    // VTK 9 stores connectivity as offsets + connectivity, which is exactly
    // what the server sent, so the cell array is handed both arrays directly.
    const polys = vtk.vtkCellArray();
    const accepted = polys.setData(
      heap.toVTKAoSArray(surface.offsets, 1, 'Offsets'),
      heap.toVTKAoSArray(surface.connectivity, 1, 'Connectivity'),
    );
    if (accepted === false) {
      throw new ViewerRuntimeError('VTK rejected the surface connectivity arrays');
    }

    const polyData = vtk.vtkPolyData();
    polyData.setPoints(points);
    polyData.setPolys(polys);

    this.#arrays = [];
    this.#entityIds = null;
    for (const array of surface.arrays) {
      const attributes =
        array.location === 'point' ? polyData.getPointData() : polyData.getCellData();
      attributes.addArray(heap.toVTKAoSArray(array.values, array.components, array.name));
      this.#arrays.push(array);
      if (array.name === ENTITY_ARRAY && array.location === 'cell' && array.components === 1) {
        // Kept on the JS side as well: the heap copy belongs to VTK and cannot
        // be read back cheaply, and every selection is a pass over these values.
        this.#entityIds = array.values;
      }
    }

    this.#polyData = polyData;
    this.#bounds = Array.from(surface.bounds);
    this.#selectionKey = null;
    this.#selectionActive = false;
    // The clipper's input is set once per surface; the slider only moves the
    // plane after this point.
    this.#clipper.setInputData(polyData);
    // rewire: a new surface has to be pushed to the mapper even when the slice
    // state itself has not changed.
    this.#applySlice({ rewire: true });
    this.setColorArray(null);
    // Armed before the fit below, so if the stage is collapsed right now the
    // fit happens when it is not rather than against a zero-height viewport.
    this.#needsCameraFit = true;
    this.resetCamera();
  }

  /**
   * Update the slice state, leaving any option that is not supplied unchanged
   * so the UI can move one control at a time.
   *
   * With the slice off the mapper reads the untouched surface, so the view is
   * identical to a viewer without this feature.
   *
   * @param {{enabled?: boolean, axis?: 'x'|'y'|'z', percent?: number}} options
   */
  setSlice({ enabled, axis, percent } = {}) {
    this.#assertLive();

    if (axis !== undefined) {
      if (!(axis in SLICE_NORMALS)) {
        throw new ViewerRuntimeError(`Unknown slice axis "${axis}"`);
      }
      this.#sliceAxis = axis;
    }
    if (enabled !== undefined) {
      this.#sliceEnabled = Boolean(enabled);
    }
    if (percent !== undefined) {
      this.#slicePercent = Number(percent);
    }

    this.#applySlice();
    this.scheduleRender();
  }

  /**
   * Move the clip plane and point the mapper at the right pipeline input.
   *
   * @param {{rewire?: boolean}} options force the mapper input to be re-set,
   *   which a newly loaded surface needs even when the slice state is unchanged
   */
  #applySlice({ rewire = false } = {}) {
    if (!this.#polyData) {
      return;
    }

    if (this.#sliceEnabled) {
      this.#clipPlane.setNormal(...SLICE_NORMALS[this.#sliceAxis]);
      this.#clipPlane.setOrigin(
        ...slicePlaneOrigin(this.#bounds, this.#sliceAxis, this.#slicePercent),
      );
    }

    if (!rewire && this.#sliceEnabled === this.#mapperReadsClipper) {
      return; // already wired the right way; only the plane moved
    }

    if (this.#sliceEnabled) {
      // GetOutputPort returns a stable vtkAlgorithmOutput, so it is fetched
      // once. Both it and SetInputConnection take an explicit port index in
      // this build.
      this.#clipOutputPort ??= this.#clipper.getOutputPort(0);
      this.#mapper.setInputConnection(0, this.#clipOutputPort);
    } else {
      this.#mapper.setInputData(this.#polyData);
    }
    this.#mapperReadsClipper = this.#sliceEnabled;
  }

  /**
   * Highlight the cells belonging to a set of geometry entity tags.
   *
   * `tags` is what `Generation.boundary_map` records for one boundary group, so
   * the caller passes the canonical value through unchanged -- the mapping from
   * a group to its regions is the platform's, and this method only colours what
   * it is given. An empty or null `tags` clears the highlight and restores
   * whichever field colouring was active.
   *
   * @param {Iterable<number>|null} tags
   * @returns {number} how many cells were highlighted; 0 means the selection
   *   matched nothing on this surface, which the caller may want to report
   */
  setSelection(tags) {
    this.#assertLive();

    const wanted = tags ? [...tags].map(Number).filter(Number.isFinite) : [];
    if (wanted.length === 0 || !this.#entityIds || !this.#polyData) {
      this.clearSelection();
      return 0;
    }

    const key = [...new Set(wanted)].sort((a, b) => a - b).join(',');
    if (key === this.#selectionKey && this.#selectionActive) {
      // Already showing exactly this. Re-uploading would put another copy of
      // the same array on the wasm heap for no visible change.
      return this.#countMatching(new Set(wanted));
    }

    const lookup = new Set(wanted);
    const ids = this.#entityIds;
    const flags = new Float32Array(ids.length);
    let matched = 0;
    for (let index = 0; index < ids.length; index += 1) {
      if (lookup.has(ids[index])) {
        flags[index] = 1;
        matched += 1;
      }
    }

    // addArray replaces an array of the same name, so each distinct selection
    // uploads once and the previous one is released by VTK.
    this.#polyData
      .getCellData()
      .addArray(this.#session.typedArrayInterface.toVTKAoSArray(flags, 1, SELECTION_ARRAY));

    this.#mapper.setScalarModeToUseCellFieldData();
    this.#mapper.setArrayAccessMode(ARRAY_ACCESS_BY_NAME);
    this.#mapper.setArrayName(SELECTION_ARRAY);
    this.#mapper.setColorModeToMapScalars();
    this.#mapper.setLookupTable(this.#selectionTable);
    this.#mapper.setScalarRange(0, 1);
    this.#mapper.scalarVisibilityOn();
    // A two-value bar reads as a legend for a field that is not one.
    this.#scalarBar.setVisibility(0);

    this.#selectionKey = key;
    this.#selectionActive = true;
    this.scheduleRender();
    return matched;
  }

  /** Drop the highlight, restoring the colouring that was in effect. */
  clearSelection() {
    this.#assertLive();
    if (!this.#selectionActive) return;
    this.#selectionActive = false;
    this.#selectionKey = null;
    this.#mapper.setLookupTable(this.#lookupTable);
    // Re-apply rather than remember: setColorArray already holds the whole
    // truth about scalar mode, range and the bar, and a second copy of that
    // reasoning here is a second thing to keep correct.
    this.setColorArray(this.#activeArray ? this.#activeArray.name : null);
  }

  #countMatching(lookup) {
    let matched = 0;
    for (const id of this.#entityIds) if (lookup.has(id)) matched += 1;
    return matched;
  }

  /**
   * Colour the surface by a field array, or by a flat colour when `name` is
   * null. Vector fields are mapped through their magnitude.
   */
  setColorArray(name) {
    this.#assertLive();
    const array = name ? this.#arrays.find((candidate) => candidate.name === name) : null;

    // A field choice made while a group is highlighted is remembered and takes
    // effect when the highlight clears: the selection is the more specific
    // statement about what the user is looking at.
    this.#activeArray = array;
    if (this.#selectionActive) {
      this.scheduleRender();
      return;
    }

    if (!array) {
      this.#mapper.scalarVisibilityOff();
      this.#scalarBar.setVisibility(0);
      this.scheduleRender();
      return;
    }

    if (array.location === 'point') {
      this.#mapper.setScalarModeToUsePointFieldData();
    } else {
      this.#mapper.setScalarModeToUseCellFieldData();
    }
    // The bundle only exposes SelectColorArray's integer-index overload, so
    // the array is selected by name through ArrayName + ArrayAccessMode
    // (1 == VTK_GET_ARRAY_BY_NAME).
    this.#mapper.setArrayAccessMode(ARRAY_ACCESS_BY_NAME);
    this.#mapper.setArrayName(array.name);
    this.#mapper.setColorModeToMapScalars();

    const [low, high] = array.range;
    // A constant field would otherwise map to a degenerate range and render
    // as a single lookup-table entry.
    const range = high > low ? [low, high] : [low, low + 1e-6];
    this.#mapper.setScalarRange(range[0], range[1]);
    this.#lookupTable.setTableRange(range);
    if (array.components > 1) {
      this.#lookupTable.setVectorModeToMagnitude();
    }
    this.#lookupTable.build();
    this.#mapper.scalarVisibilityOn();

    this.#scalarBar.setTitle(array.name);
    this.#scalarBar.setVisibility(1);
    this.scheduleRender();
  }

  /** Toggle between shaded surface and surface-with-edges. */
  setEdgeVisibility(visible) {
    this.#assertLive();
    this.#actor.getProperty().setEdgeVisibility(visible ? 1 : 0);
    this.scheduleRender();
  }

  /** Toggle wireframe representation. */
  setWireframe(wireframe) {
    this.#assertLive();
    const property = this.#actor.getProperty();
    if (wireframe) {
      property.setRepresentationToWireframe();
    } else {
      property.setRepresentationToSurface();
    }
    this.scheduleRender();
  }

  /** Frame the whole mesh and re-render. */
  resetCamera() {
    this.#assertLive();
    this.#renderer.resetCamera();
    this.#renderer.resetCameraClippingRange();
    // A fit against a collapsed viewport frames nothing, so it stays pending
    // and is repeated by #fitCameraWhenSized once the stage has a real box.
    // The Reset view button goes through here, so pressing it also satisfies a
    // deferred fit rather than leaving one queued behind it.
    const rect = this.#container.getBoundingClientRect();
    if (rect.width >= 1 && rect.height >= 1) {
      this.#needsCameraFit = false;
    }
    this.scheduleRender();
  }

  /**
   * Render on the next animation frame, collapsing bursts of state changes
   * into a single frame.
   */
  scheduleRender() {
    if (this.#disposed || this.#renderScheduled) {
      return;
    }
    this.#renderScheduled = true;
    requestAnimationFrame(() => {
      this.#renderScheduled = false;
      if (!this.#disposed) {
        this.render();
      }
    });
  }

  /** Render immediately. `render()` may suspend, so the result is awaited. */
  async render() {
    this.#assertLive();
    try {
      await this.#renderWindow.render();
    } catch (cause) {
      throw new ViewerRuntimeError(`Rendering failed: ${cause.message}`, { cause });
    }
  }

  /** Keep the drawing buffer matched to the CSS box and the display density. */
  #syncSize() {
    if (this.#disposed) {
      return;
    }
    const rect = this.#container.getBoundingClientRect();
    // A hidden or not-yet-laid-out container reports zero; sizing the render
    // window to it would produce an invalid viewport.
    if (rect.width < 1 || rect.height < 1) {
      return;
    }

    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(rect.width * ratio));
    const height = Math.max(1, Math.round(rect.height * ratio));
    if (this.#canvas.width === width && this.#canvas.height === height) {
      return;
    }

    // Only the *drawing buffer* is set here. The canvas's displayed size stays
    // with CSS (`position: absolute; inset: 0`), deliberately: writing
    // `style.width` in pixels back from a measurement taken off the same canvas
    // is the loop that let the buffer dictate the layout, and once the stage is
    // absolutely positioned the buffer is a surface VTK paints into rather than
    // a box the page is sized around.
    this.#canvas.width = width;
    this.#canvas.height = height;
    this.#renderWindow.setSize(width, height);
    this.#fitCameraWhenSized();
    this.scheduleRender();
  }

  /**
   * Fit the camera once the viewport has a real size.
   *
   * A surface can arrive while the stage is still collapsed -- `?fresh=1`, or a
   * mesh that lands before layout settles -- and `resetCamera` against a
   * degenerate viewport produces a camera fitted to nothing. The fit is
   * therefore deferred to the first resize that sees a usable box, rather than
   * being repeated on every resize: a window the user is still sizing should
   * keep the view they already framed.
   */
  #fitCameraWhenSized() {
    if (this.#disposed || this.#polyData === null || !this.#needsCameraFit) {
      return;
    }
    const rect = this.#container.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) {
      return;
    }
    this.#needsCameraFit = false;
    this.resetCamera();
  }

  #observeResize() {
    this.#resizeObserver = new ResizeObserver(() => this.#syncSize());
    this.#resizeObserver.observe(this.#container);
    this.#syncSize();
  }

  #assertLive() {
    if (this.#disposed) {
      throw new ViewerRuntimeError('The viewer scene has been disposed');
    }
  }

  /** Release the wasm session and stop observing the DOM. */
  dispose() {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;
    this.#resizeObserver?.disconnect();
    if (this.#panHandlers) {
      const { onDown, onMove, onUp, onAux } = this.#panHandlers;
      this.#canvas.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('mouseup', onUp, true);
      this.#canvas.removeEventListener('auxclick', onAux);
      this.#panHandlers = null;
    }
    try {
      this.#axesWidget?.setEnabled(0);
      this.#session.dispose();
    } catch (cause) {
      console.warn('Error while disposing the VTK session', cause);
    }
  }
}

/**
 * Load the VTK.wasm runtime and build a scene on `canvas`.
 *
 * @param {{canvas: HTMLCanvasElement, container: HTMLElement,
 *          wasmBundleUrl: string, onProgress?: (stage: string) => void}} options
 */
export async function createScene({ canvas, container, wasmBundleUrl, onProgress }) {
  onProgress?.('Loading the VTK runtime');

  let runtime;
  try {
    // The npm package is only the loader; this fetches the actual VTK 9.7
    // WebAssembly build from the configured (self-hostable) location.
    // urlIsGzip: the loader *defaults* to gzip mode and would download the
    // whole bundle, decompress it with DecompressionStream (missing on
    // iOS/WebKit < 16.4) and untar it in a worker (~3x peak memory for an
    // 82 MB runtime). A directory URL needs none of that: the loader imports
    // vtkWebAssembly.mjs directly and the glue fetches the .wasm itself.
    runtime = await loadAsync({
      url: wasmBundleUrl,
      urlIsGzip: wasmBundleUrl.endsWith('.gz'),
    });
  } catch (cause) {
    throw new ViewerRuntimeError(
      `Could not load the VTK.wasm runtime from ${wasmBundleUrl}. ` +
        'Check that the bundle URL is reachable, or set VTK_WASM_BUNDLE_URL to a self-hosted copy.',
      { cause },
    );
  }

  onProgress?.('Starting the render session');
  try {
    const session = runtime.createStandaloneSession();
    return new VtkScene({ session, canvas, container });
  } catch (cause) {
    throw new ViewerRuntimeError(`Could not start a VTK session: ${cause.message}`, { cause });
  }
}
